import React from "react";
import {
  SEARCH_MODES,
  MODE_LABELS,
  AI_MODES,
  aiTabState,
} from "./searchModes";

const ICONS = {
  [SEARCH_MODES.INDEX]: (
    <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M3 10h18M9 10v10" />
    </svg>
  ),
  [SEARCH_MODES.TEXT]: (
    <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 6h16M4 12h10M4 18h7" />
      <circle cx="17.5" cy="16.5" r="3" />
      <path d="M20 19l2 2" />
    </svg>
  ),
  [SEARCH_MODES.SEMANTIC]: (
    <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8z" />
      <path d="M19 15l.8 2.2L22 18l-2.2.8L19 21l-.8-2.2L16 18l2.2-.8z" />
    </svg>
  ),
  [SEARCH_MODES.ASSISTANT]: (
    <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 5h16a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1h-8l-5 4v-4H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1z" />
      <path d="M8 10h8M8 13h5" />
    </svg>
  ),
};

const LockIcon = () => (
  <svg role="img" aria-label="Locked" className="ds-tab-lock" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="5" y="11" width="14" height="10" rx="2" />
    <path d="M8 11V8a4 4 0 0 1 8 0v3" />
  </svg>
);

/**
 * The mode strip at the top of the Search screen. Index is always offered, Text only when the screen says so (the
 * DocumentSync_TextSearch role and an OCR'd template); the two AI modes are live, locked or hidden depending on the entitlement.
 */
export default function SearchModeTabs({
  mode,
  onChange,
  entitlement,
  hasTextSearch,
  loadingEntitlement,
  tabState,
}) {
  const tabs = [
    { id: SEARCH_MODES.INDEX, state: "live", ai: false },
    hasTextSearch
      ? { id: SEARCH_MODES.TEXT, state: "live", ai: false }
      : null,
  ]
    .filter(Boolean)
    .concat(
      AI_MODES.map((id) => ({
        id,
        ai: true,
        // The screen decides the AI tabs' state (allow-list while the server's seat gate is off); without it, the
        // entitlement alone.
        state:
          typeof tabState === "function"
            ? tabState(id)
            : loadingEntitlement
              ? "loading"
              : aiTabState(id, entitlement),
      })),
    )
    .filter((tab) => tab.state !== "hidden");

  return (
    <div className="ds-tabs" role="tablist" aria-label="Search mode">
      {tabs.map((tab) => {
        const selected = tab.id === mode;
        const locked = tab.state === "locked";
        const loading = tab.state === "loading";
        const className = [
          "ds-tab",
          selected ? "ds-tab-active" : "",
          locked ? "ds-tab-locked" : "",
          loading ? "ds-tab-loading" : "",
        ]
          .filter(Boolean)
          .join(" ");
        return (
          <button
            key={tab.id}
            id={`searchTab-${tab.id}`}
            type="button"
            role="tab"
            aria-selected={selected}
            aria-controls="searchModePanel"
            className={className}
            title={
              locked
                ? `${MODE_LABELS[tab.id]} requires a DocumentSync AI subscription`
                : MODE_LABELS[tab.id]
            }
            onClick={() => onChange(tab.id)}
          >
            {ICONS[tab.id]}
            <span className="ds-tab-label">{MODE_LABELS[tab.id]}</span>
            {tab.ai && !locked && !loading && (
              <span className="ds-ai-badge" aria-label="AI feature">
                AI
              </span>
            )}
            {tab.ai && locked && <LockIcon />}
          </button>
        );
      })}
    </div>
  );
}
